import * as React from "react";
import {
  LoaderFunctionArgs,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { LoaderData } from "./MyAccount";
import { AccountHolder, useAccount } from "../../Account";
import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  SelectChangeEvent,
  TablePagination,
  Toolbar,
  Typography,
} from "@mui/material";
import { useLanguage } from "../../Language";
import { Dayjs } from "dayjs";
import PrintIcon from "@mui/icons-material/Print";
import {
  cancelHold,
  getAllHoldsSortable,
  suspendHold,
  SuspendHoldInfo,
  unsuspendHold,
} from "../../services/Holds";
import { HostSystemItemSortType } from "../../../dto/HostSystemItemSortType";
import {
  BookJacketAndRating,
  ResourceRowHeader,
  RowHeader,
} from "./lists/ResourceListItem";
import { ResourceDWR } from "../../../dto/SuggestionResult";
import { useLocationConfiguration } from "../../LocationConfiguration";
import { TLCEventManager } from "../../../events/EventManager";
import { PrintAccountDataEvent } from "@tlc/ls2pac/app/managers/PrintAccountDataManager";
import { LS2PAC_CONSTANTS } from "src/main/resources/js/main/constants";
import { DatePicker } from "@mui/x-date-pickers";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { TLCEvents } from "../../../events/events";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { EResourcesAvailabilityClientManager } from "@tlc/ls2pac/app/ClientManagers/EResourcesAvailabilityClientManager";
import { AvailabilityFactory } from "src/main/resources/javascript/tlc/availability/AvailabilityFactory";
import { ResourceUtils } from "@tlc/ls2pac/app/utils/resourceUtils";
import { useGlobalConfig } from "../../GlobalConfig";
import { UpdateDigitalLoansButton } from "./UpdateDigitalLoansButton";
import useBackToLink from "../resource/useBackToLink";
import { HoldsSortControl, isHoldsSortType } from "./HostSystemItemSortControl";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";

// This function was taken almost 1:1 from HoldsView
// It has several typescript errors due to formatInfo being typed
// as EResourceFormat[] and not EResourceFormat
// I currently have no way to test this and confirm there is a problem
// using ts-ignore for now to compile
function onCheckoutReservedEBook(hold: HostSystemItemView[]) {
  // @ts-ignore
  let formatInfo;
  let currentHold;

  const storeFormatInfo = (hold: HostSystemItemView[]) => {
    formatInfo = hold[0].formatInfo;

    if (formatInfo) {
      // @ts-ignore
      formatInfo.formatName = formatInfo.formatId;

      if (
        // @ts-ignore
        formatInfo.formatToken &&
        // @ts-ignore
        formatInfo.formatToken != "<InvalidEResourceFormat>"
      ) {
        // @ts-ignore
        formatInfo.formatName = ResourceUtils.getMessageByMessageTokenString(
          // @ts-ignore
          formatInfo.formatToken
        );
      }
    }
  };
  // Storing format info from history table
  storeFormatInfo(hold);

  EResourcesAvailabilityClientManager.getDownloadableItemAvailability({
    downloadableResources: hold,
    resources: hold,
    callCanAbort: false,
    // @ts-ignore
    callback: function (newHold) {
      currentHold = newHold[0];
      currentHold.resource.eAvailabilities = currentHold.eAvailabilities;

      //TODO: This is assuming one availability source -- need to modify to handle more than one on one record
      if (currentHold.resource.eAvailabilities[0].formatInfo.length == 0) {
        // @ts-ignore
        currentHold.resource.eAvailabilities[0].formatInfo = formatInfo;
      }

      AvailabilityFactory.getDisplayObject(currentHold.resource);

      TLCEvents.trigger(LS2PAC_CONSTANTS.Events.onAttemptEResourceCheckout, {
        resource: currentHold.resource,
        referrer: "holds",
      });
    },
  });
}

export const holdsLoader = async ({ params, request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const sortParam = url.searchParams.get("sort");
  const sort = isHoldsSortType(sortParam)
    ? sortParam
    : HostSystemItemSortType.Status;
  const page = url.searchParams.has("page")
    ? parseInt(url.searchParams.get("page"))
    : 0;
  const pageSize = url.searchParams.has("size")
    ? parseInt(url.searchParams.get("size"))
    : 10;
  const { holds } = await getAllHoldsSortable(page, pageSize, sort, true);
  return { sort, holds, page, pageSize };
};

export interface ReadingLevelsProps {
  resource: ResourceDWR;
}
export const ReadingLevels: React.FC<ReadingLevelsProps> = ({ resource }) => {
  const { locationConfiguration } = useLocationConfiguration();
  const { translate } = useLanguage();

  if (!resource) {
    return null;
  }

  const lexileEnabled = locationConfiguration?.pacSettings?.lexileEnabled;
  const acceleratedReaderEnabled =
    locationConfiguration?.pacSettings?.acceleratedReaderEnabled;
  const { lexileDisplay } = resource.lexile;
  const { bookLevel } = resource.acceleratedReader;
  const displayBookLevel = acceleratedReaderEnabled && bookLevel;
  const displayLexile = lexileEnabled && lexileDisplay;

  return (
    <>
      {(displayLexile || displayBookLevel) && (
        <h4>
          {displayLexile && (
            <>
              {translate("lexileMeasure")}:{" "}
              <span className="lexile">{lexileDisplay}</span>
              {displayBookLevel && ", "}
            </>
          )}
          {displayBookLevel && (
            <>
              {translate("atosLevel")}:{" "}
              <span className="atos">{bookLevel}</span>
            </>
          )}
        </h4>
      )}
    </>
  );
};

export interface SerialIssueInformationProps {
  hold: HostSystemItemView;
}
export const SerialIssueInformation: React.FC<SerialIssueInformationProps> = ({
  hold,
}) => {
  const { chronology, enumeration } = hold;
  const { translate } = useLanguage();
  return (
    <>
      {chronology && chronology.length > 0 && (
        <div className="chronology">
          <span>{translate("chronology")}:</span> <strong>{chronology}</strong>
        </div>
      )}
      {enumeration && enumeration.length > 0 && (
        <div className="enumeration">
          <span>{translate("numbering")}:</span> <strong>{enumeration}</strong>
        </div>
      )}
    </>
  );
};

export const HoldStatus: React.FC<{
  hold: HostSystemItemView;
  onClickSuspend: () => void;
}> = ({ hold, onClickSuspend }) => {
  const { translate } = useLanguage();
  return (
    <span className="holdStatus">
      {hold.suspendUntilDate && (
        <>
          <span>{translate("status")}:</span>{" "}
          <strong className="suspended">{translate("suspended")}</strong>{" "}
          <span className="pickupDate">{translate("until")}:</span>{" "}
          <strong className="pickupDate">
            {hold.suspendUntilDateString}
            <Button onClick={onClickSuspend}>({translate("change")})</Button>
          </strong>
        </>
      )}
      {!hold.suspendUntilDate && (
        <>
          {hold.status === "AR" && (
            <>
              {translate("status")}:{" "}
              <span className="arrived">{translate("arrived")}</span>
            </>
          )}
          {hold.status === "T" && (
            <>
              {translate("status")}:{" "}
              <span className="inTransit">{translate("inTransit")}</span>
            </>
          )}
          {hold.status === "PE" && (
            <>
              {translate("positionInQueue")}:{" "}
              <span className="queueLength">{hold.holdQueueLength}</span>
            </>
          )}
        </>
      )}
    </span>
  );
};

export const HoldPickupInfo: React.FC<{ hold: HostSystemItemView }> = ({
  hold,
}) => {
  const { translate } = useLanguage();
  return (
    <>
      {!hold.downloadable && (
        <div className="pickupInfo">
          {translate("pickupLocation")} <strong>{hold.holdPickupBranch}</strong>{" "}
          {hold.status === "AR" &&
            !hold.suspendUntilDate &&
            hold.holdPickupBy && (
              <>
                {translate("by")}{" "}
                <strong className="pickupDate">
                  {hold.holdPickupByString}
                </strong>
              </>
            )}
        </div>
      )}
    </>
  );
};

export const HoldsRowItem: React.FC<{
  hold: HostSystemItemView;
  onClickSuspend: () => void;
  onClickCancel: () => void;
  onClickReactivate: () => void;
  onClickCheckout: () => void;
}> = ({
  hold,
  onClickSuspend,
  onClickCancel,
  onClickReactivate,
  onClickCheckout,
}) => {
  const { allowBorrowersToCancelArrivedHolds } = useGlobalConfig();
  const { translate } = useLanguage();

  const displayCancel =
    hold.status !== "T" &&
    (hold.status !== "AR" || allowBorrowersToCancelArrivedHolds);
  const displayReactivateOrSuspend =
    !hold.downloadable && hold.status !== "T" && hold.status !== "AR";
  const displayReactivate = displayReactivateOrSuspend && hold.suspendUntilDate;
  const displaySuspend = displayReactivateOrSuspend && !hold.suspendUntilDate;
  const displayHoldActions = displayCancel || displayReactivateOrSuspend;

  return (
    <>
      <BookJacketAndRating resource={hold.resource}></BookJacketAndRating>
      <div className="resourceContentWrapper lightBorder">
        <div className="resourceContent">
          {hold.resource ? (
            <ResourceRowHeader resource={hold.resource}></ResourceRowHeader>
          ) : (
            <RowHeader
              shortTitle={hold.title}
              shortAuthor={hold.author}
            ></RowHeader>
          )}
          <SerialIssueInformation hold={hold}></SerialIssueInformation>
          <ReadingLevels resource={hold.resource}></ReadingLevels>
          <HoldStatus hold={hold} onClickSuspend={onClickSuspend}></HoldStatus>
          <HoldPickupInfo hold={hold}></HoldPickupInfo>
          {displayHoldActions && (
            <div className="actionLinks">
              {displayCancel && (
                <Button onClick={onClickCancel}>
                  {translate("cancelHold")}
                </Button>
              )}
              {displayCancel && displayReactivateOrSuspend && (
                <> {translate("or")} </>
              )}
              {displayReactivate && (
                <Button onClick={onClickReactivate}>
                  {translate("reactivate")}
                </Button>
              )}
              {displaySuspend && (
                <Button onClick={onClickSuspend}>{translate("suspend")}</Button>
              )}
            </div>
          )}

          <ul className="resultActions clearfix">
            {hold.downloadable && hold.status == "AR" && (
              <li>
                <Button variant="contained" onClick={onClickCheckout}>
                  {translate("checkout")}
                </Button>{" "}
                <span className="byDate">
                  {hold.status == "AR" &&
                    !hold.suspendUntilDate &&
                    hold.holdPickupBy && (
                      <>
                        {translate("by")}{" "}
                        <strong className="pickupDate">
                          {hold.holdPickupByString}
                        </strong>
                      </>
                    )}
                </span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </>
  );
};

export interface SuspendHoldsDialogProps {
  open: boolean;
  holds: HostSystemItemView[];
  onSuspend: (suspended: SuspendHoldInfo[]) => void;
  onClose: () => void;
}
export interface SuspendHoldsFormInput {
  suspendUntil: Dayjs;
}
export const SuspendHoldsDialog: React.FC<SuspendHoldsDialogProps> = ({
  open,
  onSuspend,
  onClose,
  holds,
}) => {
  const { translate } = useLanguage();
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<SuspendHoldsFormInput>({
    mode: "onTouched",
    defaultValues: {
      suspendUntil: null,
    },
  });
  const [suspendError, setSuspendError] = React.useState<string | null>(null);

  const onSubmit: SubmitHandler<SuspendHoldsFormInput> = async (data) => {
    const { suspendUntil } = data;
    setSuspendError(null);

    try {
      const { suspendHoldInfos } = await suspendHold(
        holds,
        suspendUntil.toDate()
      );
      const errors = suspendHoldInfos.filter((hold) => hold.success === false);
      const successes = suspendHoldInfos.filter(
        (hold) => hold.success === true
      );

      if (successes.length > 0) {
        SuccessMessageControl.show(
          `${successes.length} ${translate("suspendedMessage")}`
        );
        onSuspend(successes);
        if (errors.length === 0) {
          onClose();
        }
      }

      if (errors.length > 0) {
        setSuspendError(
          `${errors.length} ${translate("suspendHoldFailureMessage")}`
        );
      }
    } catch (e) {
      setSuspendError(translate("suspendHoldFailureMessage"));
    }
  };

  const handleExited = () => {
    reset();
    setSuspendError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} onTransitionExited={handleExited}>
      {!suspendError && (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent>
            <Controller
              name="suspendUntil"
              control={control}
              rules={{ required: translate("requiredFieldError") }}
              render={({ field: { onChange, value, onBlur } }) => {
                return (
                  <DatePicker
                    autoFocus
                    slotProps={{
                      textField: {
                        error: !!errors.suspendUntil,
                        helperText: errors.suspendUntil?.message || " ",
                        required: true,
                        onBlur,
                      },
                    }}
                    onChange={onChange}
                    value={value}
                    disablePast
                    label={translate("suspend")}
                  ></DatePicker>
                );
              }}
            ></Controller>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={onClose}>
              {translate("cancel")}
            </Button>
            <Button type="submit" variant="contained">
              {translate("save")}
            </Button>
          </DialogActions>
        </form>
      )}
      {suspendError && (
        <>
          <DialogContent>
            <Alert severity="error">{suspendError}</Alert>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={onClose}>
              {translate("ok")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

interface GetAllHoldsEvent {
  total: number;
}

export const AccountHoldsPage: React.FC = () => {
  const { translate } = useLanguage();
  const { accountSummary, user, patron } = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  const { allowBorrowersToCancelArrivedHolds } = useGlobalConfig();
  const { holds, sort, page, pageSize } = useLoaderData() as LoaderData<
    typeof holdsLoader
  >;
  const [total, setTotal] = React.useState<number>(
    accountSummary.arrivedHolds + accountSummary.pendingHolds
  );

  const location = useLocation();
  useBackToLink(translate("requests"), location);

  const [holdsToSuspend, setHoldsToSuspend] = React.useState<
    HostSystemItemView[]
  >([]);

  React.useEffect(() => {
    // Until we refactor PrintAccountDataManager we need this
    const getAllHolds = async (event: GetAllHoldsEvent) => {
      try {
        const { holds } = await getAllHoldsSortable(
          0,
          event.total,
          sort,
          false
        );
        TLCEventManager.fireEvent(
          LS2PAC_CONSTANTS.Events.onGetAllHoldsResponse,
          { success: true, data: holds }
        );
      } catch (e) {
        console.error(e);
        TLCEventManager.fireEvent(
          LS2PAC_CONSTANTS.Events.onGetAllHoldsResponse,
          { success: false }
        );
      }
    };
    const getAllHoldsListenerId = TLCEventManager.addListener(
      LS2PAC_CONSTANTS.Events.onGetAllHolds,
      getAllHolds
    );

    const eResourceCheckoutResponseListenerId = TLCEvents.on(
      LS2PAC_CONSTANTS.Events.onEResourceCheckoutResponse,
      (event: { success: boolean }) => {
        if (event.success) {
          refreshPage();
          AccountHolder.fetchAccountSummaryAndPatron(true);
        }
      }
    );

    return () => {
      TLCEvents.off(
        LS2PAC_CONSTANTS.Events.onEResourceCheckoutResponse,
        eResourceCheckoutResponseListenerId
      );
      TLCEventManager.removeListener(
        LS2PAC_CONSTANTS.Events.onGetAllHolds,
        getAllHoldsListenerId
      );
    };
  }, []);

  const onClickSuspendHolds = () => {
    const suspendableHolds = selected.filter(
      (hold) => hold.status !== "T" && hold.status !== "AR"
    );
    setHoldsToSuspend(suspendableHolds);
  };

  const onClickCancelHolds = async () => {
    const cancellableHolds = selected.filter(
      (hold) =>
        hold.status !== "T" &&
        (hold.status !== "AR" || allowBorrowersToCancelArrivedHolds)
    );
    const { cancelHoldInfos } = await cancelHold(cancellableHolds);
    refreshPage();
    const successIds = cancelHoldInfos
      .filter((info) => info.success === true)
      .map((info) => info.bibliographicId);
    const successCount = successIds.length;
    const failureCount = cancelHoldInfos.filter(
      (info) => info.success === false
    ).length;
    // TODO cancelHoldFailureMessage
    if (successCount > 0) {
      SuccessMessageControl.show(
        `${successCount} ${translate("canceledMessage")}`
      );
      setSelected(
        selected.filter(
          (hold) => successIds.indexOf(hold.bibliographicId) === -1
        )
      );
      refreshPage();
      await AccountHolder.fetchAccountSummaryAndPatron(true); // force update of counts
    }
  };

  const onClickReactivateHolds = async () => {
    const { suspendHoldInfos } = await unsuspendHold(selected);

    const successIds = suspendHoldInfos
      .filter((info) => info.success === true)
      .map((info) => info.bibliographicId);
    const successCount = successIds.length;
    setSelected(
      selected.filter((hold) => successIds.indexOf(hold.bibliographicId) === -1)
    );
    const failureCount = suspendHoldInfos.filter(
      (info) => info.success === false
    ).length;
    // TODO unsuspendHoldFailureMessage
    if (successCount > 0) {
      SuccessMessageControl.show(
        `${successCount} ${translate("unsuspendMessage")}`
      );
      refreshPage();
    }
  };

  const onClickPrintHolds = () => {
    TLCEventManager.fireEvent(PrintAccountDataEvent, {
      sections: [LS2PAC_CONSTANTS.MyAccountPrintTypes.holds],
    });
  };

  const [displayedRows, setDisplayedRows] = React.useState(holds);
  // when holds value from loader changes because of a URL param change update displayedRows state
  React.useEffect(() => {
    setDisplayedRows(holds);
  }, [holds]);

  const [selected, setSelected] = React.useState<HostSystemItemView[]>([]);
  const selectedRows = React.useMemo<string[]>(
    () => selected.map((row) => row.holdNumber),
    [selected]
  );

  const handleSelectAllClick = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      const { holds } = await getAllHoldsSortable(
        0,
        total,
        HostSystemItemSortType.Author
      );
      setSelected(holds);
      return;
    }
    setSelected([]);
  };

  const isSelected = (holdNumber: string) =>
    selectedRows.indexOf(holdNumber) !== -1;

  const handleClick = (
    event: React.MouseEvent<unknown>,
    hold: HostSystemItemView
  ) => {
    const { holdNumber } = hold;
    const selectedIndex = selectedRows.indexOf(holdNumber);

    if (selectedIndex === -1) {
      setSelected([...selected, hold]);
    } else {
      setSelected(selected.filter((tc) => tc.holdNumber !== holdNumber));
    }
  };

  const refreshPage = async () => {
    const { holds } = await getAllHoldsSortable(page, pageSize, sort);
    setDisplayedRows(holds);
  };

  const handleSortChange = async function (event: SelectChangeEvent) {
    const newSort = event.target.value as HostSystemItemSortType;
    searchParams.set("page", "0");
    searchParams.set("sort", newSort);
    setSearchParams(searchParams);
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
  };

  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    searchParams.set("page", "0");
    searchParams.set("size", event.target.value);
    setSearchParams(searchParams);
  };

  const reactivateDisabled = React.useMemo(() => {
    if (selected.length === 0) {
      return true;
    }

    // disable if NONE of the selected holds have suspend dates
    return !selected.some((value) => {
      return !!value.suspendUntilDate;
    });
  }, [selected]);

  const suspendDisabled = React.useMemo(() => {
    if (selected.length === 0) {
      return true;
    }

    // disable if every hold is arrived or in transit
    return selected.every((hold) => {
      return hold.status == "AR" || hold.status == "T";
    });
  }, [selected]);

  const cancelDisabled = React.useMemo(() => {
    if (selected.length === 0) {
      return true;
    }

    // Disable if every hold is in transit, or arrived and allowBorrowersToCancelArrivedHolds is also disabled
    return selected.every((hold) => {
      return (
        (hold.status == "AR" && !allowBorrowersToCancelArrivedHolds) ||
        hold.status == "T"
      );
    });
  }, [selected]);

  return (
    <Box>
      {total === 0 && (
        <Alert severity="info">
          {translate("noHoldsMessage")} {translate("request")}.
        </Alert>
      )}
      {total > 0 && (
        <>
          <Toolbar
            sx={{
              flexWrap: "wrap",
              gap: 1,
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
              ...(selected.length > 0 && {
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    theme.palette.action.activatedOpacity
                  ),
              }),
            }}
          >
            <Checkbox
              color="primary"
              indeterminate={selected.length > 0 && selected.length < total}
              checked={total > 0 && selected.length === total}
              onChange={handleSelectAllClick}
              inputProps={{
                "aria-label": translate("selectAll"),
              }}
            />
            {selected.length === 0 && (
              <>
                <Typography
                  sx={{ flex: "1 1", textWrap: "nowrap" }}
                  color="inherit"
                  variant="h6"
                >
                  {translate("requests")}: {patron.pendingHolds}{" "}
                  <Box
                    component={"span"}
                    sx={{
                      color:
                        patron.arrivedHolds > 0 ? "success.main" : "inherit",
                    }}
                  >
                    {translate("arrived")}: {patron.arrivedHolds}
                  </Box>
                </Typography>
                <UpdateDigitalLoansButton
                  patronId={patron.patronId}
                  onUpdate={() => {
                    AccountHolder.fetchAccountData(true);
                  }}
                  lastUpdated={accountSummary.digitalInformationLastUpdated}
                ></UpdateDigitalLoansButton>
                <Button
                  onClick={onClickPrintHolds}
                  disabled={total === 0}
                  startIcon={<PrintIcon />}
                >
                  {translate("print")} {translate("requests")}
                </Button>
                <HoldsSortControl
                  value={sort}
                  onChange={handleSortChange}
                ></HoldsSortControl>
              </>
            )}
            {selected.length > 0 && (
              <>
                <Typography
                  sx={{ flex: "1 1", textWrap: "nowrap" }}
                  color="inherit"
                  variant="subtitle1"
                  component="div"
                >
                  {selected.length} {translate("selected")}
                </Typography>
                <Button
                  disabled={reactivateDisabled}
                  onClick={onClickReactivateHolds}
                >
                  {translate("reactivate")}
                </Button>
                <Button
                  disabled={suspendDisabled}
                  onClick={onClickSuspendHolds}
                >
                  {translate("suspend")}
                </Button>
                <Button disabled={cancelDisabled} onClick={onClickCancelHolds}>
                  {translate("cancelHold")}
                </Button>
              </>
            )}
          </Toolbar>
          <div className="resultItems">
            <ul>
              {displayedRows.map((row, index) => {
                const isItemSelected = isSelected(row.holdNumber);

                return (
                  <li
                    key={row.holdNumber}
                    className="item mainResultItem clearfix"
                  >
                    <Box>
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onClick={(event) => {
                          handleClick(event, row);
                        }}
                        inputProps={{
                          "aria-label": translate("select"),
                        }}
                      />
                    </Box>

                    <HoldsRowItem
                      hold={row}
                      key={row.holdNumber}
                      onClickCheckout={() => {
                        onCheckoutReservedEBook([row]);
                      }}
                      onClickCancel={async () => {
                        const { cancelHoldInfos } = await cancelHold([row]);
                        if (cancelHoldInfos[0].success) {
                          refreshPage();
                          SuccessMessageControl.show(
                            `1 ${translate("canceledMessage")}`
                          );
                          // TODO deterimine page
                          refreshPage();
                          await AccountHolder.fetchAccountSummaryAndPatron(
                            true
                          ); // force update of counts
                        } else {
                          // TODO cancelHoldFailureMessage
                        }
                      }}
                      onClickReactivate={async () => {
                        const { suspendHoldInfos } = await unsuspendHold([row]);
                        if (suspendHoldInfos[0].success) {
                          SuccessMessageControl.show(
                            `1 ${translate("unsuspendMessage")}`
                          );
                          refreshPage();
                        } else {
                          // TODO unsuspendHoldFailureMessage
                        }
                      }}
                      onClickSuspend={() => setHoldsToSuspend([row])}
                    ></HoldsRowItem>
                  </li>
                );
              })}
            </ul>
          </div>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      <SuspendHoldsDialog
        holds={holdsToSuspend}
        open={holdsToSuspend.length > 0}
        onClose={() => setHoldsToSuspend([])}
        onSuspend={(suspended) => {
          const suspendedBibliographicIds = suspended.map(
            (hold) => hold.bibliographicId
          );
          setSelected(
            selected.filter(
              (hold) =>
                suspendedBibliographicIds.indexOf(hold.bibliographicId) === -1
            )
          );
          refreshPage();
        }}
      ></SuspendHoldsDialog>
    </Box>
  );
};
